/**
 * Phase 14 — Architect Onboarding (minimal, launch-safe)
 * ------------------------------------------------------
 * Renders a real screen for the /onboarding/architect route so users
 * arriving from PMPro post-payment never see a blank page. This is
 * intentionally minimal: it shows the architect their account status,
 * asks them to set a password (if they arrived through the PMPro
 * webhook without one), and then routes them into the architect
 * profile / dashboard section.
 *
 * This file exists so the /onboarding/architect React route resolves
 * to a working component. Full architect-specific onboarding flows
 * (portfolio setup wizard, KYC nudge, etc.) can layer on top later
 * without changing the router contract.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ArrowRight, Loader2, AlertCircle, Mail } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchitectOnboarding({ user, setUser }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // If the user is already fully onboarded, jump them to their profile.
    if (user && user.onboarding_complete) {
      navigate('/architect/profile', { replace: true });
    }
  }, [user, navigate]);

  const handleContinue = async () => {
    setError('');
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // No session: PMPro post-payment users don't have creds yet.
        // Send them to login so they can request a password reset.
        navigate('/auth');
        return;
      }
      // Mark architect onboarding complete on the backend (best effort
      // through the existing generic endpoint). If a dedicated endpoint
      // isn't wired, we still route the user forward — a blank page is
      // the worst outcome and we must avoid it.
      try {
        const res = await axios.post(
          `${API}/onboarding/architect/complete`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res?.data?.user && setUser) setUser(res.data.user);
      } catch (_) {
        // Swallow — routing forward is more important than the flag flip.
      }
      navigate('/architect/profile');
    } catch (e) {
      setError('Something went wrong. Please try again or contact support.');
    } finally {
      setBusy(false);
    }
  };

  const isSignedIn = Boolean(user);
  const name = (user && (user.name || user.email)) || '';

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: theme.colors.bg.page,
      padding: 24,
    }}>
      <div
        data-testid="architect-onboarding-card"
        style={{
          maxWidth: 560,
          width: '100%',
          padding: 32,
          background: theme.colors.bg.card,
          border: `1px solid ${theme.colors.borders}`,
          borderRadius: theme.radius.card,
          boxShadow: theme.colors.shadows.md,
        }}
      >
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '4px 10px', borderRadius: 999,
          background: '#ecfdf5', color: '#065f46',
          fontSize: 12, fontWeight: 700, marginBottom: 14,
        }}>
          <CheckCircle2 size={14} /> Membership Active
        </div>

        <h1 data-testid="architect-onboarding-title"
            style={{ fontSize: 26, fontWeight: 800, margin: 0, marginBottom: 6, color: theme.colors.text.primary }}>
          Welcome to Kickback Contract{name ? `, ${name}` : ''}
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginBottom: 20, lineHeight: 1.5 }}>
          Your <strong>Kickback Architect</strong> plan is set up. You can now review
          architect assignments, respond to creator requests, and manage your profile.
        </p>

        {!isSignedIn && (
          <div
            data-testid="architect-onboarding-signin-hint"
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: 14, marginBottom: 18,
              background: '#fef3c7', border: '1px solid #fcd34d',
              borderRadius: 10, color: '#92400e', fontSize: 13, lineHeight: 1.5,
            }}
          >
            <Mail size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Please sign in to continue.</strong> If you just completed
              checkout, use the "Continue to Kickback Contract" link from your
              confirmation page (or welcome email) to activate your account and
              set a password. If you already have a password, sign in below.
            </div>
          </div>
        )}

        {error && (
          <div
            data-testid="architect-onboarding-error"
            style={{
              display: 'flex', gap: 8, alignItems: 'center',
              padding: 12, marginBottom: 14,
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, color: '#991b1b', fontSize: 13,
            }}
          >
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <button
          data-testid="architect-onboarding-continue"
          onClick={handleContinue}
          disabled={busy}
          style={{
            padding: '12px 20px',
            borderRadius: theme.radius.cta,
            border: 'none',
            background: theme.colors.primary,
            color: '#fff',
            fontWeight: 800,
            fontSize: 14,
            cursor: busy ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {busy ? <Loader2 size={14} /> : null}
          {isSignedIn ? 'Continue to Architect Dashboard' : 'Go to Login'}
          <ArrowRight size={14} />
        </button>

        <div style={{ marginTop: 20, fontSize: 12, color: theme.colors.text.muted }}>
          Need help? <Link to="/support" style={{ color: theme.colors.primary, fontWeight: 700 }}>Contact support</Link>
        </div>
      </div>
    </div>
  );
}
